/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'

import { DateTime } from 'luxon'
import { FaDoorOpen, FaThermometerHalf } from 'react-icons/fa'
import { FiSliders } from 'react-icons/fi'
import { TbSnowflake } from 'react-icons/tb'

import type { HistoryWindow } from '@/components/charts/history/useHistoryNavigation'
import { Alert } from '@/components/ui'
import ActiveAlertsCard from '@/components/ui/asset-detail/ActiveAlertsCard'
import DetailCard, { type DetailItem } from '@/components/ui/asset-detail/DetailCard'
import { DetailSheet, type DetailSheetProps } from '@/components/ui/detail-sheet/DetailSheet'
import { useApi, useDashboard } from '@/hooks'
import TrackerHistoryChart from '@/pages/trackers/map/historyGraph/TrackerHistoryChart'
import type { TrackerHistory } from '@/pages/trackers/map/types'
import type { ApiService } from '@/types/dashboard-types'

import { fetchHumidityHistory, fetchTemperatureHistory } from './api'
import { assetOfSensor, useColdStorage } from './ColdStorageContext'
import { DOOR_HISTORY_HOURS, DoorHistoryTable } from './DoorHistoryTable'
import {
  type Units,
  doorLabel,
  formatPercent,
  formatRange,
  formatTemp,
  formatTime,
  formatTiming,
  isOfflineTemperature,
  sensorStatus,
  toAlertRow,
} from './format'
import { CloseButton } from './LocationDetail'
import type { HistoryPoint } from './types'
import { useNow } from './useNow'

const DEFAULT_HOURS = 7 * 24

const toSamples = (points: HistoryPoint[]) =>
  points.map((p) => ({ time: new Date(p.time).toISOString(), value: p.value }))

/** The two sensor history series in the shape the tracker chart draws. */
export const toChartHistory = (
  window: HistoryWindow,
  temperature: HistoryPoint[],
  humidity: HistoryPoint[]
): TrackerHistory => ({
  from: window.from.toISO() ?? '',
  to: window.to.toISO() ?? '',
  temperature: toSamples(temperature),
  humidity: toSamples(humidity),
  light: [],
  tilt: [],
  pressure: [],
  battery: [],
  location: [],
})

const na = (value: string): Pick<DetailItem, 'value' | 'valueClassName'> =>
  value === '—' ? { value, valueClassName: 'text-base-content/40' } : { value }

/**
 * A sensor's current state and its history. A temperature sensor's history is its
 * temperature and, where the point reports it, humidity, drawn by the tracker history
 * chart with the location's range as the min/max marklines. A door sensor's is a table
 * of its openings over the last 24 hours. Shown in the detail sheet over the floor plan.
 */
export const SensorDetail = ({ tableHeader }: Pick<DetailSheetProps, 'tableHeader'>) => {
  const { api } = useApi()
  const context = useColdStorage()
  const units = useDashboard().configuration.units as Units
  const sensorId = context.selectedSensorId
  const asset = sensorId != null ? assetOfSensor(context.assets, sensorId) : undefined
  const sensor = asset?.sensors.find((s) => s.id === sensorId)
  const show = context.showSensorModal && !!sensor && !!asset
  const now = useNow(show && context.mode.showTiming)

  const [activeTab, setActiveTab] = useState<'status' | 'graph'>('status')
  const [chartData, setChartData] = useState<TrackerHistory>()
  const [chartError, setChartError] = useState('')
  const [isChartLoading, setIsChartLoading] = useState(false)
  // Remount the chart when the details close so they reopen on the default window.
  const [chartResetKey, setChartResetKey] = useState(0)

  const defaultWindow = useMemo<HistoryWindow>(() => {
    const to = DateTime.now().startOf('hour').plus({ hours: 1 })
    return { from: to.minus({ hours: DEFAULT_HOURS }), to }
    // Keyed on the sensor so a newly opened sensor gets a fresh window.
  }, [sensorId])

  useEffect(() => {
    if (!show) {
      setChartResetKey((k) => k + 1)
      setActiveTab('status')
      setChartData(undefined)
    }
  }, [show])

  const fetchChart = useCallback(
    async (window: HistoryWindow): Promise<TrackerHistory | undefined> => {
      if (sensorId == null || !sensor || sensor.type !== 'temperature') return undefined
      const [temperature, humidity] = await Promise.all([
        fetchTemperatureHistory(api, sensorId, window.from, window.to),
        sensor.humidity?.enabled ? fetchHumidityHistory(api, sensorId, window.from, window.to) : Promise.resolve([]),
      ])
      return toChartHistory(window, temperature, humidity)
    },
    [api, sensorId, sensor]
  )

  // The chart owns its window after the first load; the result is not pushed back into props.
  const reloadChart = useCallback(
    async (_: ApiService, window: HistoryWindow) => {
      try {
        return await fetchChart(window)
      } catch {
        return undefined
      }
    },
    [fetchChart]
  )

  useEffect(() => {
    if (!show || activeTab !== 'graph' || chartData) return
    let cancelled = false
    setChartError('')
    setIsChartLoading(true)
    fetchChart(defaultWindow)
      .then((data) => {
        if (!cancelled) setChartData(data)
      })
      .catch(() => {
        if (!cancelled) setChartError('Unable to load the sensor history.')
      })
      .finally(() => {
        if (!cancelled) setIsChartLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [show, activeTab, chartData, fetchChart, defaultWindow])

  if (!show || !sensor || !asset) return null

  const status = sensorStatus(sensor, asset.threshold)
  const timing = formatTiming(sensor.timing, now)
  // Blast freezer: how long the sensor has been in range, or how long until it is.
  const timingItems: DetailItem[] =
    context.mode.showTiming && sensor.type === 'temperature'
      ? [
          {
            label: timing.estimate ? 'Est. time to range' : 'Time in range',
            value: timing.text,
            valueClassName: timing.estimate
              ? 'italic text-base-content/60'
              : timing.text === '—'
                ? 'text-base-content/40'
                : undefined,
          },
        ]
      : []
  const isDoor = sensor.type === 'door'
  const offline = isOfflineTemperature(sensor)
  const readingItems: DetailItem[] = isDoor
    ? [
        { label: 'Status', ...na(doorLabel(sensor)) },
        { label: 'Last reported', ...na(formatTime(sensor.sampleTime)) },
      ]
    : offline
      ? [
          // An offline tag's reading is stale: show it as the last known one, never as current.
          { label: 'Last known temperature', ...na(formatTemp(sensor.temperatureCelsius, units)) },
          // Offline since is almost always the last reading's time, so it stands in for
          // Last reported; a tag that never recorded going offline falls back to it.
          sensor.offlineSince
            ? { label: 'Offline since', value: formatTime(sensor.offlineSince), valueClassName: 'text-error' }
            : { label: 'Last reported', ...na(formatTime(sensor.sampleTime)) },
        ]
      : [
          {
            label: 'Temperature',
            value:
              sensor.tagState && sensor.tagState !== 'ok'
                ? `Fault (${sensor.tagState})`
                : formatTemp(sensor.temperatureCelsius, units),
            valueClassName: status === 'alarm' ? 'text-error font-semibold' : undefined,
          },
          ...timingItems,
          ...(sensor.humidity?.enabled ? [{ label: 'Humidity', ...na(formatPercent(sensor.humidity.percent)) }] : []),
          { label: 'Last reported', ...na(formatTime(sensor.sampleTime)) },
        ]
  const tagItems: DetailItem[] = [
    { label: 'Tag', ...na(sensor.tagId != null ? `#${sensor.tagId}` : '—') },
    { label: 'Serial number', ...na(sensor.serialNumber != null ? String(sensor.serialNumber) : '—') },
    { label: 'Online', ...na(sensor.online == null ? '—' : sensor.online ? 'Yes' : 'No') },
  ]
  const locationItems: DetailItem[] = [
    { label: 'Location', value: asset.name },
    { label: 'Facility', ...na(asset.location?.name ?? '—') },
    {
      label: 'Gateway',
      ...na(asset.gateway ? `${asset.gateway.deviceName} (${asset.gateway.online ? 'online' : 'offline'})` : '—'),
    },
    // A door has no temperature, so the location's range says nothing about it.
    ...(isDoor
      ? []
      : [
          {
            label: 'Acceptable range',
            value: (
              <button
                type="button"
                className="btn btn-ghost btn-xs gap-1 font-normal"
                onClick={() => context.setRangeLocationId(asset.id)}
              >
                {formatRange(asset.threshold, units)}
                <FiSliders className="w-3.5 h-3.5" />
              </button>
            ),
          },
        ]),
  ]

  return (
    <DetailSheet
      isOpen
      tableHeader={tableHeader}
      bodyKey={sensor.id}
      header={
        <div className="flex justify-between w-full items-center gap-4 min-w-0">
          <span className="truncate font-semibold">
            {sensor.name} · {asset.name}
          </span>
          <div className="flex items-center gap-3 shrink-0">
            <span className="text-xs text-primary-content/80">{isDoor ? 'Door Sensor' : 'Temperature Sensor'}</span>
            <CloseButton onClick={() => context.setShowSensorModal(false)} />
          </div>
        </div>
      }
    >
      <div className="tabs tabs-border content-start">
        <input
          type="radio"
          name="sensor_tabs"
          className="tab mb-4"
          aria-label="Status"
          checked={activeTab === 'status'}
          onChange={() => setActiveTab('status')}
        />
        <div className="tab-content min-h-[400px]">
          <div className="@container">
            <div className="grid grid-cols-1 @3xl:grid-cols-3 gap-4 content-start">
              {isDoor ? (
                <DetailCard icon={<FaDoorOpen />} title="Door" items={readingItems} />
              ) : (
                <DetailCard icon={<FaThermometerHalf />} title="Reading" items={readingItems} />
              )}
              <DetailCard icon={<TbSnowflake />} title="Location" items={locationItems} />
              <DetailCard icon={<FiSliders />} title="Tag" items={tagItems} />
              <ActiveAlertsCard alerts={sensor.activeAlerts.map(toAlertRow)} className="@3xl:col-span-3" />
            </div>
          </div>
        </div>

        <input
          type="radio"
          name="sensor_tabs"
          className="tab mb-4"
          aria-label={isDoor ? 'History' : 'Graph'}
          checked={activeTab === 'graph'}
          onChange={() => setActiveTab('graph')}
        />
        <div className="tab-content min-h-[400px]">
          {activeTab === 'graph' && isDoor && context.companyId != null && (
            <div className="flex flex-col gap-2">
              <p className="text-xs text-base-content/60">Door openings in the last {DOOR_HISTORY_HOURS} hours</p>
              <DoorHistoryTable companyId={context.companyId} assetId={asset.id} monitoringPointId={sensor.id} />
            </div>
          )}
          {activeTab === 'graph' && !isDoor && (
            <div className="relative">
              {chartData ? (
                <TrackerHistoryChart
                  key={chartResetKey}
                  data={chartData}
                  from={defaultWindow.from}
                  periodHours={DEFAULT_HOURS}
                  units={units}
                  shipmentRange={
                    asset.threshold ? { min: asset.threshold.lowCelsius, max: asset.threshold.highCelsius } : undefined
                  }
                  reload={reloadChart}
                />
              ) : chartError ? (
                <Alert type="error" message={chartError} />
              ) : !isChartLoading ? (
                <Alert type="info" message="No history for this sensor." />
              ) : (
                <div className="h-[300px]" />
              )}
              {isChartLoading && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-white/70">
                  <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-blue-600" />
                  <p className="text-sm text-gray-600">Loading history…</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </DetailSheet>
  )
}
